import React from "react";

import "./App.css";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import DefaultLayout from "./DefaultLayout";
import Home from "./pages/Home";
import Prospects from "./pages/Prospects";
import ProspectDetail from "./pages/ProspectDetail";
import ProspectImport from "./pages/ProspectImport";
import FindProspects from "./pages/FindProspects";
import Teams from "./pages/Teams";
import Campaigns from "./pages/Campaigns/Campaigns";
import Settings from "./pages/Settings";
import "react-toastify/dist/ReactToastify.css";
import CreateCampaign from "./pages/Campaigns/CreateCampaign";
import CampaignListing from "./pages/CampaignManagement/CampaignListing";
import CampaignCreateLayout from "./pages/CampaignManagement/CampaignCreateLayout";
import CampaignCreateLeadsStep from "./pages/CampaignManagement/CampaignCreateLeadsStep";
import CampaignCreateSendersStep from "./pages/CampaignManagement/CampaignCreateSendersStep";
import CampaignCreateSequenceStep from "./pages/CampaignManagement/CampaignCreateSequenceStep";
import CampaignCreateScheduleStep from "./pages/CampaignManagement/CampaignCreateScheduleStep";
import CampaignCreateReviewLaunchStep from "./pages/CampaignManagement/CampaignCreateReviewLaunchStep";
import CampaignViewDetails from "./pages/CampaignManagement/CampaignViewDetails";
import ImportProgressLoader from "./components/ImportProgressLoader";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import ExpiredPlanModal from "./components/Modals/ExpiredPlanModal";

import Billings from "./pages/Billings";
import Inbox from "./pages/Inbox";
import AuthLoginPage from "./pages/auth/Login";
import AuthRegisterPage from "./pages/auth/Register";
import AuthForgotPasswordPage from "./pages/auth/ForgotPassword";
import AuthResetPasswordPage from "./pages/auth/ResetPassword";
import NotificationsPage from "./pages/Notifications";
import LeadsDatabase from "./pages/LeadsDatabase";
import Templates from "./pages/Templates";
import Integrations from "./pages/Integrations";
import ErrorLogsPage from "./pages/ErrorLogs";
import DailyReportsPage from "./pages/DailyReports";
import EmailCampaignAnalyticsPage from "./pages/EmailCampaignAnalytics";
import EmailOpenTrackPage from "./pages/EmailOpenTrackPage";
import PipelineOverview from "./pages/PipelineOverview";
import SequenceTemplateEditor from "./pages/SequenceTemplateEditor";
import AiContentLayout from "./pages/AiContent/AiContentLayout";
import StrategyPage from "./pages/AiContent/StrategyPage";
import PlanPage from "./pages/AiContent/PlanPage";
import ScheduledPage from "./pages/AiContent/ScheduledPage";
import HistoryPage from "./pages/AiContent/HistoryPage";
import RequireAiPosterAccess from "./pages/AiContent/RequireAiPosterAccess";
import PaymentSuccessPage from "./pages/payment/PaymentSuccess";
import PaymentCancelledPage from "./pages/payment/PaymentCancelled";
import { RedirectIfAuthenticated, RequireAuth } from "./routes/RouteGuards";
import { sanitizeStoredAuth } from "./utils/authStorage";
const DashboardLoader = () => {
  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ zIndex: 10000 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 bg-white z-50 flex items-center justify-center"
    >
      <div className="flex flex-col items-center gap-5">
        <div className="relative flex h-20 w-20 items-center justify-center">
          <motion.div
            aria-hidden="true"
            className="absolute inset-0 rounded-[22px] bg-[#C8F24A]/25"
            animate={{ scale: [1, 1.18, 1], opacity: [0.35, 0.7, 0.35] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.img
            src="/logo/loading.svg"
            alt="Innlead"
            className="relative h-16 w-16 rounded-[18px] shadow-sm"
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-sm text-dark-grey"
        >
          Loading Innlead...
        </motion.p>
      </div>
    </motion.div>
  );
};

function App() {
  const [importSuccess, setimportSuccess] = useState(0);
  const [importScanned, setimportScanned] = useState(0);
  const [importDuplicates, setimportDuplicates] = useState(0);
  const [importFalsePositives, setimportFalsePositives] = useState(0);
  const [importPercent, setimportPercent] = useState(0);
  const [visibility, setVisibility] = useState("hidden");
  let location = useLocation();

  useEffect(() => {
    sanitizeStoredAuth();
  }, []);

  let utils = {
    setimportSuccess,
    setimportScanned,
    setimportDuplicates,
    setimportFalsePositives,
    setimportPercent,
    setVisibility,
  };

  const [showModal, setShowModal] = useState(false);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // Adjust this time as needed

    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      <AnimatePresence>
        {isLoading && <DashboardLoader />}
      </AnimatePresence>
      <ImportProgressLoader
        visibility={visibility}
        success={importSuccess}
        scanned={importScanned}
        duplicates={importDuplicates}
        falsePositives={importFalsePositives}
        percent={importPercent}
      />
      <ExpiredPlanModal show={showModal} onClose={handleCloseModal} />
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated>
              <AuthLoginPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/register"
          element={
            <RedirectIfAuthenticated>
              <AuthRegisterPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RedirectIfAuthenticated>
              <AuthForgotPasswordPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/reset-password"
          element={
            <RedirectIfAuthenticated>
              <AuthResetPasswordPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancelled" element={<PaymentCancelledPage />} />
        <Route path="/email/open/:token" element={<EmailOpenTrackPage />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <DefaultLayout />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Home />} />
          <Route path="/pipeline" element={<PipelineOverview />} />
          <Route path="/leads" element={<LeadsDatabase />} />
          <Route path="/prospects/find" element={<FindProspects />} />
          <Route
            path="/prospects"
            element={
              <Prospects utils={utils} setShowExpiredAlert={setShowModal} />
            }
          />
          <Route
            path="/prospects/:listId"
            element={
              <ProspectDetail utils={utils} setShowExpiredAlert={setShowModal} />
            }
          />
          <Route
            path="/prospects/:listId/import"
            element={
              <ProspectImport utils={utils} setShowExpiredAlert={setShowModal} />
            }
          />
          <Route path="/teams" element={<Teams />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/inbox/thread/:threadId" element={<Inbox />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/templates/create" element={<SequenceTemplateEditor />} />
          <Route path="/templates/:templateId" element={<SequenceTemplateEditor />} />
          <Route path="/daily-reports" element={<DailyReportsPage />} />
          <Route path="/analytics/email-campaigns" element={<EmailCampaignAnalyticsPage />} />
          <Route path="/error-logs" element={<ErrorLogsPage />} />
          <Route path="/campaign-management" element={<CampaignListing />} />
          <Route path="/campaign-management/view/:campaignId" element={<CampaignViewDetails />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route
            path="/ai-content"
            element={
              <RequireAiPosterAccess>
                <AiContentLayout />
              </RequireAiPosterAccess>
            }
          >
            <Route index element={<Navigate to="/ai-content/plan" replace />} />
            <Route path="strategy" element={<StrategyPage />} />
            <Route path="plan" element={<PlanPage />} />
            <Route path="scheduled" element={<ScheduledPage />} />
            <Route path="history" element={<HistoryPage />} />
          </Route>
          <Route path="/campaign-management/create" element={<CampaignCreateLayout />}>
            <Route index element={<CampaignCreateLeadsStep />} />
            <Route path="senders" element={<CampaignCreateSendersStep />} />
            <Route
              path="sequence"
              element={<CampaignCreateSequenceStep />}
            />
            <Route
              path="schedule"
              element={<CampaignCreateScheduleStep />}
            />
            <Route
              path="review-launch"
              element={<CampaignCreateReviewLaunchStep />}
            />
          </Route>
          <Route
            path="/billings"
            element={
              <Navigate
                to={{ pathname: "/settings/billing", search: location.search }}
                replace
              />
            }
          />

          <Route path="/settings">
            <Route index element={<Navigate to="/settings/profile" />} />
            <Route path="profile" element={<Settings />} />
            <Route path="account-activity" element={<Settings />} />

            <Route path="billing" element={<Settings />} />
          </Route>
          <Route path="/campaigns">
            <Route index element={<Navigate to="/campaigns/list/running" />} />
            <Route path="list" element={<Campaigns setShowExpiredAlert={setShowModal}/>}>
              <Route path=":id" element={<Campaigns setShowExpiredAlert={setShowModal}/>} />
            </Route>
            <Route path="template" element={<Campaigns setShowExpiredAlert={setShowModal}/>} />
            <Route
              path="create"
              element={<CreateCampaign setShowExpiredAlert={setShowModal} />}
            />
          </Route>

          <Route path="*" element={<Navigate to="/" />} />
        </Route>
      </Routes>
    </div>
  );
}

export default App;
